import { Stack, Typography, Box } from '@mui/material'
import { format } from 'date-fns'
import type React from 'react'

import type { OpenBankingConnectionFailure } from '@/types/Activity'
import type { StatusTypes } from '@/types/Recommendation'

interface RequestCardTimelineProps {
  recommendation: {
    createdAt?: string
    updatedAt?: string
    status?: StatusTypes | null
    completed?: boolean
    connectionFailures?: OpenBankingConnectionFailure[] | null
  }
  activities: Array<{ timestamp?: string }>
  type: 'active' | 'completed'
  requestTypeText: string
}

export const RequestCardTimeline: React.FC<RequestCardTimelineProps> = ({
  recommendation,
  activities,
  type,
  requestTypeText,
}) => {
  const hasConnected = recommendation.status === 'Complete'

  const requestCreatedItem = recommendation.createdAt
    ? [{ label: 'Connection requested on:', timestamp: recommendation.createdAt }]
    : []

  const reminderItems = activities
    .filter(activity => activity && activity.timestamp)
    .map((activity, index) => ({
      label: `We sent reminder email #${index + 1} on:`,
      timestamp: activity.timestamp,
    }))

  const connectionFailureItems = (recommendation.connectionFailures ?? [])
    .filter(failure => failure && failure.timestamp)
    .map(failure => ({
      label: failure.reason,
      timestamp: failure.timestamp,
    }))

  const reminderAndFailureItems = [...reminderItems, ...connectionFailureItems].sort(
    (a, b) => new Date(a.timestamp ?? 0).getTime() - new Date(b.timestamp ?? 0).getTime(),
  )

  const requestCompletedItem =
    type === 'completed' && recommendation.updatedAt
      ? [
          {
            label: hasConnected
              ? `${requestTypeText} connected:`
              : `${requestTypeText} request cancelled:`,
            timestamp: recommendation.updatedAt,
          },
        ]
      : []

  const timelineItems = [
    ...requestCreatedItem,
    ...reminderAndFailureItems,
    ...requestCompletedItem,
  ]

  if (timelineItems.length === 0) {
    return null
  }

  return (
    <Stack spacing={0}>
      {timelineItems.map((item, idx) => {
        const dateText = item.timestamp
          ? format(new Date(item.timestamp), "dd/MM/yyyy 'at' HH:mm")
          : ''

        return (
          <Stack
            key={`${item.label}-${item.timestamp}-${idx}`}
            direction="row"
            spacing={2}
            sx={{
              minHeight: 32,
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 10,
                minWidth: 10,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#bdbdbd',
                }}
              />
            </Box>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ flex: 1 }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#29222a',
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{ fontSize: '12px', color: '#534e54', flexShrink: 0, ml: 1 }}
              >
                {dateText}
              </Typography>
            </Stack>
          </Stack>
        )
      })}
    </Stack>
  )
}

export default RequestCardTimeline
